import BarChart from "@mui/icons-material/BarChart";
import InfoOutline from "@mui/icons-material/InfoOutline";
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, type ChangeEvent, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { ErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import type { MomentWhenClassification } from "../../moment-card/components/moment-card-when";
import type { FollowUp } from "../server/follow-up-service";
import type { FollowUpMomentDetails } from "../server/types";

export const FollowUpSidebarReason: FC<{
  value: FollowUp["config"];
  onChange(reason: FollowUp["config"]): void;
  source_id: string;
  surveys: FollowUpMomentDetails["surveys"];
  error: ErrorProps;
  disabled: boolean;
  whenClassification: MomentWhenClassification;
}> = ({ value, onChange, source_id, surveys, error, disabled, whenClassification }) => {
  const { t } = useTranslation();

  const handleChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, val: string) => {
      // Parse string back into the expected config object
      if (val === "no_interaction") {
        onChange({ type: "no_interaction", source_id });
      } else if (val.startsWith("no_survey_completion:")) {
        const surveyId = val.split(":")[1];
        if (!surveyId) return;
        onChange({ type: "no_survey_completion", survey_id: surveyId, source_id });
      }
    },
    [onChange, source_id],
  );

  const actualValue = useMemo(() => {
    if (!value) return "";

    if (value.type === "no_interaction") return "no_interaction";
    if (value.type === "no_survey_completion") return `no_survey_completion:${value.survey_id}`;

    return "";
  }, [value]);

  return (
    <FormControl error={error?.error} disabled={disabled}>
      <Stack direction="column" spacing={1}>
        <Typography id="follow-up-reason-label" variant="subtitle2">
          Select a Follow-Up reason
        </Typography>

        <RadioGroup
          aria-labelledby="follow-up-reason-label"
          name="follow-up-reason-group"
          onChange={handleChange}
          value={actualValue}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <Paper elevation={5} sx={{ p: 1, width: "100%" }}>
              <FormControlLabel
                value="no_interaction"
                control={<Radio size="small" />}
                label={
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography variant="body2">
                      {t(
                        whenClassification.type === "triggered"
                          ? "Most recent Communication not seen"
                          : "Communication not seen",
                      )}
                    </Typography>
                    <Tooltip
                      title={t(
                        '"Seen" means clicking a link within a communication, opening an email communication, or reacting to a Slack or Microsoft Teams message with an emoji.',
                      )}
                    >
                      <InfoOutline fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                }
              />
            </Paper>

            {(surveys || []).map((survey) => (
              <Paper key={survey.id} elevation={5} sx={{ width: "100%" }}>
                <Stack direction="column" spacing={0}>
                  <FormControlLabel
                    value={`no_survey_completion:${survey.id}`}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {t(
                          whenClassification.type === "triggered"
                            ? "Most recent Survey incomplete"
                            : "Survey incomplete",
                        )}
                      </Typography>
                    }
                    sx={{ p: 2 }}
                  />
                  <Divider />
                  <Stack direction="row" spacing={1} sx={{ p: 3 }} alignItems="center">
                    <BarChart fontSize="small" />
                    <Typography variant="subtitle2">{survey.title}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </RadioGroup>
        {error.helperText && <FormHelperText>{error.helperText}</FormHelperText>}
      </Stack>
    </FormControl>
  );
};
